import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Stack,
  Title,
  Button,
  Group,
  Text,
  Avatar,
  Badge,
  Card,
  Progress,
  ActionIcon,
  SimpleGrid,
  Paper,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconEdit,
  IconBook,
  IconCards,
  IconTrendingUp,
  IconStar,
  IconClock,
  IconCalendar,
  IconUser,
  IconMicrophone,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { parentApi } from "@/features/parent/api/parentApi";
import { useAuthStore } from "@/features/auth/store/authStore";
import { ParentQuickReadButton } from "@/features/recommendations";
import { capitalizeName } from "@/utils/name";

export function MobileChildDetailPage() {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);

  const { data: children, isLoading } = useQuery({
    queryKey: ["children", user?.id],
    queryFn: () => parentApi.getChildren(),
    enabled: !!user?.id,
  });

  const child = children?.find((c) => c.id === childId);

  if (isLoading) {
    return (
      <Container size="sm" p="md">
        <Text size="sm" c="dimmed" ta="center">
          Loading child details...
        </Text>
      </Container>
    );
  }

  if (!child) {
    return (
      <Container size="sm" p="md">
        <Stack gap="md" align="center" mt={40}>
          <Text size="sm" c="dimmed">
            Child not found
          </Text>
          <Button onClick={() => navigate("/mobile/parent/children")}>Back to Children</Button>
        </Stack>
      </Container>
    );
  }

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return "green";
    if (progress >= 50) return "blue";
    return "yellow";
  };

  const getStreakBadgeColor = (streak: number) => {
    if (streak >= 30) return "violet";
    if (streak >= 7) return "orange";
    if (streak >= 3) return "yellow";
    return "gray";
  };

  return (
    <Container size="sm" p="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <ActionIcon onClick={() => navigate(-1)} variant="subtle" size="lg">
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Title order={2}>Child Details</Title>
          <ActionIcon
            onClick={() => navigate(`/mobile/parent/child/${childId}/edit`)}
            variant="subtle"
            size="lg"
          >
            <IconEdit size={20} />
          </ActionIcon>
        </Group>

        {/* Child Info Card */}
        <Card p="lg" withBorder>
          <Stack gap="md" align="center">
            <Avatar size={80} radius="xl" color="blue">
              {child.avatar ? (
                <img src={child.avatar} alt={capitalizeName(child.name)} />
              ) : (
                <IconUser size={40} />
              )}
            </Avatar>
            <div style={{ textAlign: "center" }}>
              <Title order={3}>{capitalizeName(child.name)}</Title>
              {child.grade && (
                <Badge size="lg" variant="light" mt="xs">
                  Grade {child.grade}
                </Badge>
              )}
            </div>
            {child.lastActive && (
              <Group gap={4}>
                <IconClock size={14} color="var(--mantine-color-dimmed)" />
                <Text size="sm" c="dimmed">
                  Last active {child.lastActive}
                </Text>
              </Group>
            )}
          </Stack>
        </Card>

        {/* Reading Streak */}
        {child.readingStreak !== undefined && child.readingStreak > 0 && (
          <Card p="md" withBorder>
            <Group justify="space-between">
              <Group gap="xs">
                <IconStar size={20} color="var(--mantine-color-orange-6)" />
                <Text size="md" fw={500}>
                  Reading Streak
                </Text>
              </Group>
              <Badge size="xl" variant="light" color={getStreakBadgeColor(child.readingStreak)}>
                {child.readingStreak} days
              </Badge>
            </Group>
          </Card>
        )}

        {/* Quick Actions */}
        <Stack gap="xs">
          <ParentQuickReadButton
            studentId={childId || null}
            childName={capitalizeName(child.name)}
            size="md"
            fullWidth
          />
          {child.fluency_enabled && (
            <Button
              size="md"
              variant="light"
              color="grape"
              leftSection={<IconMicrophone size={18} />}
              fullWidth
              onClick={() => navigate(`/parent/fluency/start?studentId=${child.id}`)}
            >
              Read Aloud
            </Button>
          )}
          <SimpleGrid cols={2} gap="xs">
            <Button
              variant="light"
              leftSection={<IconBook size={16} />}
              onClick={() => navigate(`/mobile/parent/assign/story?childId=${childId}`)}
              fullWidth
            >
              Assign Story
            </Button>
            <Button
              variant="light"
              leftSection={<IconCards size={16} />}
              onClick={() => navigate(`/mobile/parent/assign/flashcards?childId=${childId}`)}
              fullWidth
            >
              Assign Flashcards
            </Button>
          </SimpleGrid>
        </Stack>

        {/* Stats */}
        {child.stats && (
          <Card p="md" withBorder>
            <Stack gap="md">
              <Text size="md" fw={600}>
                Progress Overview
              </Text>

              {/* Stories Progress */}
              {child.stats.storiesTotal && (
                <div>
                  <Group justify="space-between" mb={6}>
                    <Group gap="xs">
                      <IconBook size={16} />
                      <Text size="sm">Stories Read</Text>
                    </Group>
                    <Text size="sm" fw={500}>
                      {child.stats.storiesRead}/{child.stats.storiesTotal}
                    </Text>
                  </Group>
                  <Progress
                    value={(child.stats.storiesRead / child.stats.storiesTotal) * 100}
                    color={getProgressColor(
                      (child.stats.storiesRead / child.stats.storiesTotal) * 100
                    )}
                    size="md"
                  />
                </div>
              )}

              {/* Cards Progress */}
              {child.stats.cardsTotal && (
                <div>
                  <Group justify="space-between" mb={6}>
                    <Group gap="xs">
                      <IconCards size={16} />
                      <Text size="sm">Cards Mastered</Text>
                    </Group>
                    <Text size="sm" fw={500}>
                      {child.stats.cardsMastered}/{child.stats.cardsTotal}
                    </Text>
                  </Group>
                  <Progress
                    value={(child.stats.cardsMastered / child.stats.cardsTotal) * 100}
                    color={getProgressColor(
                      (child.stats.cardsMastered / child.stats.cardsTotal) * 100
                    )}
                    size="md"
                  />
                </div>
              )}

              {/* Weekly Goal */}
              {child.stats.weeklyGoal && (
                <div>
                  <Group justify="space-between" mb={6}>
                    <Group gap="xs">
                      <IconTrendingUp size={16} />
                      <Text size="sm">Weekly Goal</Text>
                    </Group>
                    <Text size="sm" fw={500}>
                      {child.stats.weeklyProgress || 0}/{child.stats.weeklyGoal} sessions
                    </Text>
                  </Group>
                  <Progress
                    value={((child.stats.weeklyProgress || 0) / child.stats.weeklyGoal) * 100}
                    color={getProgressColor(
                      ((child.stats.weeklyProgress || 0) / child.stats.weeklyGoal) * 100
                    )}
                    size="md"
                  />
                </div>
              )}

              {/* Quiz Average */}
              {child.stats.avgQuizScore !== undefined && (
                <Group justify="space-between">
                  <Text size="sm">Average Quiz Score</Text>
                  <Badge
                    size="lg"
                    variant="light"
                    color={getProgressColor(child.stats.avgQuizScore)}
                  >
                    {child.stats.avgQuizScore}%
                  </Badge>
                </Group>
              )}
            </Stack>
          </Card>
        )}

        {/* Next Assignment */}
        {child.nextAssignment && (
          <Card p="md" withBorder>
            <Stack gap="xs">
              <Text size="md" fw={600}>
                Next Assignment
              </Text>
              <Paper p="sm" withBorder style={{ borderStyle: "dashed" }}>
                <Group gap="xs">
                  {child.nextAssignment.type === "story" && <IconBook size={18} />}
                  {child.nextAssignment.type === "flashcards" && <IconCards size={18} />}
                  <div style={{ flex: 1 }}>
                    <Text size="sm" fw={500}>
                      {child.nextAssignment.title}
                    </Text>
                    <Group gap={4} mt={4}>
                      <IconCalendar size={12} color="var(--mantine-color-dimmed)" />
                      <Text size="xs" c="dimmed">
                        Due {new Date(child.nextAssignment.dueDate).toLocaleDateString()}
                      </Text>
                    </Group>
                  </div>
                </Group>
              </Paper>
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
